import {
  Box,
  Button,
  Flex,
  Icon,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { HiArrowRight } from 'react-icons/hi';

import { ExternalLink } from '$components/chakra/external-link';
import { Input } from '$components/form/input';
import { Section } from '$components/layout/section';
import { Textarea } from '$components/form/text-area';
import { FadeIn } from '$components/ui/fade-in';
import { sendEmail } from '$http/send-email';
import { social } from '$shared/social';

import { contactFormSchema } from './form-schema';

interface ContactFormData {
  email: string;
  name: string;
  message: string;
}

export function Contact() {
  const toast = useToast({ position: 'bottom-left' });

  const { register, handleSubmit, formState, reset } = useForm<ContactFormData>(
    {
      resolver: yupResolver(contactFormSchema),
    },
  );

  const handleContact = handleSubmit(async (data) => {
    const response = await sendEmail(data);

    if (response.ok) {
      toast({
        title: 'Mensagem enviada.',
        description: 'Vou responder em breve.',
        status: 'success',
        isClosable: true,
        duration: 3000,
      });
      reset();
    } else {
      toast({
        title: 'Algo deu errado.',
        description: `Tente novamente em alguns instantes ou escreva para ${social.email}.`,
        status: 'error',
        isClosable: true,
        duration: 8000,
      });
    }
  });

  return (
    <Section dataScroll="contact" eyebrow="Contato" title="Vamos conversar.">
      <FadeIn>
        <Text fontSize="17px" color="text.muted" maxW="56ch" mb="32px">
          Se for sobre construir algo bem feito, seja produto, ferramenta
          interna ou uma ideia em fase de forma, me escreva. Costumo responder
          em até 48h.
        </Text>
      </FadeIn>

      <Stack as="form" onSubmit={handleContact} spacing="24px" maxW="640px">
        <Flex direction={{ base: 'column', md: 'row' }} gap="20px">
          <Input
            {...register('name')}
            label="Nome"
            placeholder="Como devo te chamar?"
            error={formState.errors.name}
          />
          <Input
            {...register('email')}
            label="Email"
            placeholder="voce@empresa.com"
            error={formState.errors.email}
          />
        </Flex>

        <Textarea
          {...register('message')}
          id="message"
          label="Mensagem"
          placeholder="Conte um pouco do contexto…"
          error={formState.errors.message}
        />

        <Box>
          <Button
            type="submit"
            variant="solid"
            rightIcon={<Icon as={HiArrowRight} />}
            isLoading={formState.isSubmitting}
          >
            Enviar mensagem
          </Button>
        </Box>
      </Stack>

      <Box mt="40px">
        <Text
          fontFamily="mono"
          fontSize="11px"
          letterSpacing="0.16em"
          textTransform="uppercase"
          color="text.faint"
          mb="12px"
        >
          Ou pelos canais diretos
        </Text>
        <Flex gap="20px" wrap="wrap" fontSize="14px">
          <ExternalLink href={`mailto:${social.email}`}>
            {social.email}
          </ExternalLink>
          <ExternalLink href={social.linkedin}>LinkedIn</ExternalLink>
          <ExternalLink href={social.github}>GitHub</ExternalLink>
        </Flex>
      </Box>
    </Section>
  );
}
